import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { useGetInvestmentFundQuery } from 'api/main';
import { getSubscriptionData, getSubscriptionNAV, parseNumber, parseSum } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { StyledTableRow } from 'components';
import { TableCell } from '@mui/material';

const PortfolioRow = ({
    subscription,
    capitalAccounts,
    shareSubcategories,
    capitalCalls,
    closings,
    hideColumns,
    navs,
}) => {
    const { investingSum } = subscription.investmentCharacteristics;
    const { data: fund } = useGetInvestmentFundQuery(subscription?.investmentFund?.id, {
        skip: !subscription?.investmentFund?.id,
    });

    const subscriptionNAV = useMemo(
        () => getSubscriptionNAV({ subscription, navs }),
        [navs, subscription]
    );

    const {
        investmentSumCalled,
        sharesNumber,
        valuation,
        latestLiquidityDate, // latest capital account issue date
        subcategory,
    } = getSubscriptionData({
        subscription,
        shareSubcategories,
        capitalAccounts,
        capitalCalls,
        closings,
        fund,
        subscriptionNAV,
    });
    const dpi = investmentSumCalled !== 0 ? 0 / investmentSumCalled : 0; // 0 represents distributions, to be added later
    const rvpi = valuation / investmentSumCalled;
    const tvpi = dpi + rvpi;
    const irr =
        isNaN(subscription.internalRateOfReturn?.rate) || !subscription.internalRateOfReturn?.rate
            ? null
            : subscription.internalRateOfReturn.rate;
    const liquidityDate = useMemo(() => {
        if (fund?.evergreen) {
            return latestLiquidityDate ? moment(latestLiquidityDate).format('L') : '-';
        } else {
            return latestLiquidityDate ? moment().format('L') : '-';
        }
    }, [fund, latestLiquidityDate]);

    return (
        <StyledTableRow>
            <TableCell>{subscription.investmentFund.name}</TableCell>
            <TableCell>{moment(subscription.finishedSubscriptionAt).format('L')}</TableCell>
            <TableCell>{sharesNumber ? parseNumber(sharesNumber, 3) : '-'}</TableCell>
            <TableCell>{subcategory?.name}</TableCell>
            <TableCell>{parseSum(investingSum)}</TableCell>
            <TableCell>{parseSum(investmentSumCalled)}</TableCell>
            <TableCell>{parseSum(investingSum - investmentSumCalled)}</TableCell>
            <TableCell>{liquidityDate}</TableCell>
            <TableCell>
                <b>{valuation === 0 ? '-' : parseSum(valuation)}</b>
            </TableCell>
            <TableCell>-</TableCell>
            <TableCell>{irr ? parseNumber(irr * 100, 1) + '%' : '-'}</TableCell>
            <TableCell>{dpi === 0 ? '-' : dpi.toFixed(2)}</TableCell>
            <TableCell>{valuation === 0 ? '-' : rvpi.toFixed(2)}</TableCell>
            <TableCell>{tvpi ? tvpi.toFixed(2) : '-'}</TableCell>
            {!hideColumns && (
                <TableCell>
                    {subscription.distributorCompany?.corporateName || texts.PEQAN}
                </TableCell>
            )}
        </StyledTableRow>
    );
};

PortfolioRow.propTypes = {
    subscription: PropTypes.object,
    capitalAccounts: PropTypes.array,
    shareSubcategories: PropTypes.array,
    capitalCalls: PropTypes.array,
    closings: PropTypes.array,
    navs: PropTypes.array,
    hideColumns: PropTypes.bool,
};

export default PortfolioRow;
